<template>
    <div class="mt-16 space-y-16">
        <banner
            v-if="!disabled && hasBulkTimeslots && showAlert('bulkAlert')"
            :description="$t('interview.alerts.bulk_warning', { application_list: applicationList })"
            inline
            warning
        />

        <div v-if="!disabled && !form.instant && !hasBulkTimeslots">
            <div
                class="border rounded-6 p-12"
                :class="[form.slot_confirmed ? 'border-secondary' : 'border-primary-20', form.calendar_open ? 'opacity-50' : '']"
            >
                <checkbox
                    v-model="form.slot_confirmed"
                    :disabled="form.calendar_open"
                    class="mr-8 whitespace-nowrap disabled:text-primary-20"
                >
                    <span class="font-normal">
                        {{ $t('interview.application_confirmed_slot.checkbox') }}
                    </span>
                </checkbox>
            </div>
        </div>

        <banner
            v-if="showAlert('countAlert')"
            :description="$t('mailer.more_candidates_then_timeslots')"
            class="-mt-2 mb-20"
            inline
            warning
        />

        <simple-input
            v-model="duration"
            ref="duration"
            type="number"
            class="grow"
            :disabled="disabled"
            :min="5"
            :step="5"
            required
        >
            {{ `${$t('interview.duration')} (${$t('interview.minutes')})` }}
        </simple-input>

        <div>
            <label class="mb-8 text-body-sm flex items-center">
                <span class="first-letter:uppercase text-black font-semibold">
                    {{ $t('interview.time_slots') }}
                </span>
            </label>
            <template v-for="(slot, i) in creating ? form.time_slots : timeslots">
                <div v-if="!form.slot_confirmed || (form.slot_confirmed && i === 0)"
                     class="flex gap-12 mb-4 relative"
                >
                    <div class="grow flex">
                        <date-picker
                            v-model="slot.date"
                            class="grow"
                            format="LLLL"
                            time
                            :enable-auto-format="false"
                            :show-warning-icon="timeslotWarning(slot)"
                            :warning-message="timeslotWarningMessage(slot)"
                        />
                        <div class="flex items-center justify-center px-10 font-semibold text-primary-20">
                            {{ $t('interview.until') }}
                        </div>
                        <div @click="$refs.duration.focus()" class="grow">
                            <date-picker
                                class="w-full pointer-events-none"
                                format="LLLL"
                                time
                                :value="formatEndDate(slot)"
                                :enable-auto-format="false"
                            />
                        </div>
                    </div>

                    <div v-if="!value.instant" class="flex justify-end">
                        <div v-if="!slot.available" class="px-8 flex items-center">
                            <div>
                                <tooltip
                                    :content="$t('interview.timeslots.taken_by', {
                                        application_fullname: getApplicationName(slot.selected_application_id)
                                    })"
                                    position="left"
                                >
                                <span>
                                    <svg-icon class="w-16 h-16 text-secondary" type="check-circle"/>
                                </span>
                                </tooltip>
                            </div>
                        </div>
                        <div v-if="!disabled && (slot.available || form.selected_timeslot_id === slot.id)" class="flex items-center justify-between">
                            <span class="text-semantic-error hover:text-semantic-error-light cursor-pointer" @click="removeTimeSlot(i)">
                                <svg-icon class="w-16 h-16" type="cancel"/>
                            </span>
                        </div>
                    </div>

                </div>
            </template>

            <div v-if="!disabled && !form.slot_confirmed" class="flex">
                <div class="flex justify-center group font-semibold pt-16 w-full">
                    <simple-button
                        icon="add-circle"
                        @click="addTimeSlots"
                        small
                    >
                        {{ $t('interview.add_time_slot') }}
                    </simple-button>
                </div>
            </div>
        </div>

        <div class="flex justify-end items-center gap-8">
            <simple-button
                look="text"
                v-if="hasDatesInThePast && !value.instant"
                @click="removeTimeslotsInPast"
            >
                {{ $t('interview.timeslots.delete_in_past') }}
            </simple-button>
            <div v-if="!disabled && !creating">
                <simple-button
                    display="secondary"
                    look="primary"
                    v-if="canManage"
                    @click="saveTimeslots"
                >
                    {{ $t('general.confirm') }}
                </simple-button>
            </div>
        </div>

        <popup-modal-confirm v-if="showDatesInPastWarning"
                             :message="$t('interviews.timeslots.in_past.description')"
                             confirm-button="success"
                             @close="closeWarning"
                             @confirm="closeWarning"
        >
            <p class="mt-16 font-semibold">{{ $t('interviews.timeslots.in_past.action') }}</p>
        </popup-modal-confirm>

        <popup-modal-confirm v-if="showWarning"
                             :has-close-button="true"
                             :message="$t('interview.remove_timeslot_title')"
                             :show-cancel-button="false"
                             :title="$t('interview.remove_timeslot_description')"
                             confirm-button="success"
                             @close="closeWarning"
                             @confirm="closeWarning"
        />
    </div>
</template>

<script>
import Tooltip from "./navigation/Tooltip";
import SvgIcon from './ui/SvgIcon';
import DatePicker from './forms/DatePicker';
import Input from './forms/Input';
import Form from '../etc/forms/Form';
import moment from 'moment';
import SimpleButton from './ui/SimpleButton';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import Checkbox from "./forms/Checkbox";
import * as calendar from "../services/CalendarService";
import {mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import User from "../models/User";
import Banner from "./ui/Banner.vue";

export default {
    components: {
        Banner,
        PopupModalConfirm,
        SimpleButton,
        'simple-input': Input,
        DatePicker,
        SvgIcon,
        Checkbox,
        Tooltip
    },
    props: {
        value: Form,
        subs: {type: Array, default: []},
        update: Boolean,
        alerts: {type: Object, default: null},
        hasBulkTimeslots: {type: Boolean, default: false},
        bulkApplications: {
            type: Array, default: function () {
                return [];
            }
        },
        canManage: {type: Boolean, default: false},
        disabled: {type: Boolean, default: false},
        creating: Boolean
    },
    data() {
        return {
            keepChanges: false,
            toggleResetEvent: false,
            editableSlotId: null,
            showWarning: false,
            showDatesInPastWarning: false,
            timeslots: [],
            time_slots_remove: [],
            duration: 30,
            availablePeriods: [],
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
        form: {
            get() {
                return this.value;
            },
            set(form) {
                this.$emit('input', form);
            },
        },
        user() {
            return useRepo(User)
                .with('calendars')
                .with('calendar_profiles', (q) => {
                    q.with('calendars');
                })
                .find(this.currentUserId);
        },
        hasDatesInThePast() {
            return this.timeslots.filter(slot => this.dateIsInPast(slot)).length;
        },
        hasTakenTimeslot() {
            return this.timeslots.filter(slot => !slot.available).length
        },
        applicationList() {
            if (!this.bulkApplications.length) return;
            let str = '';
            this.bulkApplications.forEach((app, index) => {
                if (index) str += ', ';
                str += app.attributes.name;
            })
            return str;
        }
    },
    methods: {
        showAlert(alert) {
            if (!this.alerts) return false;
            return this.alerts[alert] ?? false;
        },
        addTimeSlots() {
            const lastTimeslotDate = moment.max(this.timeslots.map(d => moment(d.date)));
            const date = moment(lastTimeslotDate)
                .add(this.duration, 'minutes')
                .set({second:0,millisecond:0});

            this.timeslots.push({
                id: null,
                date: date,
                available: true,
            });

            if (this.creating) {
                this.form.time_slots = this.timeslots;
            }
        },
        removeTimeSlot(i) {
            if (this.timeslots.length === 1) {
                this.showWarning = true;
                return false;
            }

            if (this.timeslots[i].id) {
                this.time_slots_remove.push(this.timeslots[i]);
            }

            this.timeslots.splice(i, 1);

            if (this.creating) {
                this.form.time_slots = this.timeslots;
            }
        },
        removeTimeslotsInPast() {
            this.timeslots = this.timeslots.filter(slot => !this.dateIsInPast(slot));
            this.form.time_slots = this.timeslots;
        },
        isSlotDisabled(slot) {
            return (!slot.available && this.form.selected_timeslot_id !== slot.id) || this.disabled;
        },
        dateIsInPast(slot) {
            return moment(slot.date).isBefore(moment());
        },
        timeslotIsNotAvailableForAll(slot) {
            if (!this.noCronofySubs) {
                return !this.availablePeriods.find(availableSlot => {
                    return moment(slot.date).isBetween(moment(availableSlot.start), moment(availableSlot.end), null, '[]') &&
                        moment(slot.date).add(this.duration, 'minutes').isBetween(moment(availableSlot.start), moment(availableSlot.end), null, '[]');
                })
            }
        },
        timeslotWarning(slot) {
            return !this.value.instant && (this.dateIsInPast(slot) || this.timeslotIsNotAvailableForAll(slot));
        },
        timeslotWarningMessage(slot) {
            if (this.dateIsInPast(slot) && !this.value.instant) {
                  return this.$t('form.date.in_past');
            } else if (this.timeslotIsNotAvailableForAll(slot) && !this.value.instant) {
                return this.$t('interview.timeslots.not_available_for_all.single.description');
              }
        },
        formatEndDate(slot) {
            if (!slot.available && slot.id !== this.form.selected_timeslot_id) {
                return moment(slot.date)
                    .local()
                    .add(this.form.original.duration, 'minutes');
            } else {
                return moment(slot.date)
                    .local()
                    .add(this.duration, 'minutes');
            }

        },
        closeWarning() {
            this.showWarning = false;
            this.showDatesInPastWarning = false;
        },
        saveTimeslots() {
            if (this.hasDatesInThePast && !this.value.instant) {
                this.showDatesInPastWarning = true;
                return;
            }
            this.keepChanges = true;
            this.form.duration = this.duration;
            this.form.time_slots = this.timeslots;
            this.form.time_slots_remove = this.time_slots_remove;
            this.$emit('saved');
        },
        getApplicationName(id) {
            const filteredApplication = this.bulkApplications.find(application => application.id === id);
            return filteredApplication ? filteredApplication.attributes.name : null;
        },
        getAvailabilities() {
            if (!this.form.time_slots.length) return;

            const subs = [...this.subs.filter(sub => sub.user.cronofy_id && sub.user.availability_rules.length)]
                .map(sub => {
                    return {
                        sub: sub.user.cronofy_id,
                        managed_availability: true,
                        availability_rule_ids: ['work_hours']
                    }
                });

            if (!subs.length) {
                this.noCronofySubs = true;
                this.renderCalendar = false;
                return;
            }

            this.noCronofySubs = false;

            let timeslots = [...this.form.time_slots]

            // order timeslots by date
            timeslots.sort((a, b) => {
                return new moment(a.date).diff(new moment(b.date));
            });

            let firstTimeslot = timeslots[0].date,
                lastTimeslot = timeslots[timeslots.length - 1].date;

            let start = new moment(firstTimeslot),
                end = new moment(lastTimeslot).add(this.duration, 'minutes');

            // make sure start date is not in the past
            if (start.isBefore(moment())) {
                start = moment();
            }

            // if end date is in the past, don't query availabilities, all timeslots are in the past
            if (end.isBefore(moment())) {
                this.availablePeriods = [];
                return;
            }

            let queryPeriods = [{start: start.format(), end: end.format()}];

            calendar.availabilities({
                availability_query: {
                    participants: [
                        {
                            required: "all",
                            members: subs
                        }
                    ],
                    max_results: 512,
                    required_duration: { minutes: this.form.duration },
                    query_periods: queryPeriods,
                },
                locale: this.locale,
            })
                .then(({available_periods}) => this.availablePeriods = available_periods);
        },
    },
    watch: {
        'duration'() {
            if (this.creating) {
                this.form.duration = this.duration;
            }
        },
        'form.time_slots': {
            handler() {
                this.getAvailabilities();
            },
            deep: true
        },
    },
    created() {
        this.getAvailabilities();
    },
    mounted() {
        this.duration = this.form.duration;
        this.timeslots = this.form.time_slots;
    },
    beforeUnmount() {
        // if (!this.keepChanges) {
            this.form.options.save_current_only = false;
        // }
        this.keepChanges = false;
    }
};
</script>

<style scoped>
.custom-absolute-icon {
    position: absolute;
    left: -22px;
    top: 12px;
}
</style>
